import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { PlusLg } from 'react-bootstrap-icons';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import { Pagination } from '../../shared/components/Pagination';
import { ERole } from '../../shared/enums/ERole';
import { handleError } from '../../shared/functions/handleError';
import { usePagination } from '../../shared/hooks/usePagination';
import { ITrailer } from '../../shared/interfaces/ITrailer';
import { IUser } from '../../shared/interfaces/IUser';
import { RootState } from '../../store/store';
import TrailerForm from './components/TrailerForm';
import { AdminTrailerService } from './service';
import TrailerTable from './components/TrailerTable';

const schema = yup.object({
  licensePlate: yup.string().required('License plate is required'),

  technicalInspection: yup
    .string()
    .required('Technical inspection is required')
    .matches(
      /^\d{4}-\d{2}-\d{2}$/,
      'Technical inspection must be a valid date in YYYY-MM-DD format',
    ),

  frc: yup
    .string()
    .required('FRC date is required')
    .matches(
      /^\d{4}-\d{2}-\d{2}$/,
      'FRC must be a valid date in YYYY-MM-DD format',
    ),
});

const AdminTrailerPage = () => {
  const { t } = useTranslation();
  const { role } = useSelector(
    (state: RootState) => state.auth.userData,
  ) as IUser;
  const isAdmin = role === ERole.ADMIN;
  const [isActionModalOpened, setIsActionModalOpened] = useState(false);

  const {
    register,
    watch,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ITrailer>({
    resolver: yupResolver(schema),
  });
  const {
    pagination,
    setTotalPages,
    setCount,
    changeFilterHandler,
    totalPages,
    page,
    handlePages,
    handleUpdateList,
  } = usePagination({
    perPage: 10,
  });
  const [trailers, setTrailers] = useState<ITrailer[]>([]);

  const handleCloseActionModal = () => {
    setIsActionModalOpened(false);
    reset({
      licensePlate: '',
    });
  };
  const handleSubmitActionModal = async (data: ITrailer): Promise<void> => {
    const isUpdate = !!data?.id;
    await AdminTrailerService[isUpdate ? 'update' : 'create'](data)
      .then((response) => {
        if (response) {
          reset({
            licensePlate: '',
          });
          handleUpdateList();
          handleCloseActionModal();
          toast.success(
            t(`toast.${isUpdate ? 'successUpdated' : 'successCreated'}`, {
              name: response?.licensePlate,
            }),
          );
        }
      })
      .catch(handleError);
  };
  useEffect(() => {
    AdminTrailerService.getAll(pagination)
      .then((response) => {
        setTrailers(response?.data);
        setTotalPages(Math.ceil(response?.count / response?.perPage));
      })
      .catch(handleError);
  }, [pagination, setCount, setTotalPages]);
  return (
    <div>
      <div className="mb-3">
        <div className="w-100 d-flex justify-content-between align-items-center">
          {isAdmin && (
            <Button
              variant={'success'}
              className={'d-flex align-items-center'}
              onClick={() => setIsActionModalOpened(true)}
            >
              <PlusLg className={'me-1'} />
              {t('button.create')}
            </Button>
          )}
          <div className={'d-flex align-items-end'}>
            <Form.Control
              type="text"
              className={'w-100'}
              onChange={(e) =>
                changeFilterHandler('licensePlate', e.target.value)
              }
              placeholder={t('general.searchByLicensePlate')}
            />
          </div>
        </div>
      </div>
      <TrailerTable
        trailers={trailers}
        isAdmin={isAdmin}
        reset={reset}
        setIsActionModalOpened={setIsActionModalOpened}
        handleUpdateList={handleUpdateList}
      />

      <Pagination
        page={page}
        totalPages={totalPages}
        handlePagination={handlePages}
        changeFilterHandler={changeFilterHandler}
      />
      <Modal
        show={isActionModalOpened}
        onHide={handleCloseActionModal}
        size={'lg'}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t(watch('id') ? 'general.update' : 'general.create')}{' '}
            {t('general.trailer')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <TrailerForm register={register} errors={errors} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseActionModal}>
            {t('button.close')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit(handleSubmitActionModal)}
          >
            {t(watch('id') ? 'button.update' : 'button.create')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminTrailerPage;
